import { memo, useCallback, useMemo } from 'react';

import { useFilters } from 'hooks/useFilters';

import {
  useGetBestGamesByRetentionQuery,
  useGetPeriodsQuery,
  useGetRegionsQuery,
} from 'store/api/configService/analyticsController';

import {
  appendTopGameColors,
  mapOptions,
  sortCountryOptions,
} from 'utils/analytics';

import FilterToggleGroup from 'components/UI/FilterToggleGroup/FilterToggleGroup';
import Loader from 'components/UI/Loader/Loader';
import NoData from 'components/UI/NoData/NoData';
import Typography from 'components/UI/Typography/Typography';
import Filters from 'components/pages/Analytics/Filters/Filters';
import GameBars from 'components/pages/Analytics/GameBars/GameBars';
import GamesList from 'components/pages/Analytics/GamesByRetention/GamesList/GamesList';
import Section from 'components/pages/Analytics/Layout/Section/Section';
import SectionContent from 'components/pages/Analytics/Layout/SectionContent/SectionContent';
import SectionHeader from 'components/pages/Analytics/Layout/SectionHeader/SectionHeader';

import {
  DEFAULT_VALUES,
  LIMITS,
  OPTIONS,
  TOP_GAME_COLORS,
} from 'components/config/analytics';

import styles from './GamesByRetention.module.scss';

const defaultValues = DEFAULT_VALUES.byRetention;

const GamesByRetention = () => {
  const {
    filters: { type, ...filters },
    setFilters,
    handleChange,
  } = useFilters({
    type: OPTIONS.gameTypes[0].value,
    retentionPeriod: defaultValues.period,
    cohortJoinPeriod: defaultValues.cohortJoinPeriod,
    filterBy: OPTIONS.retentionFilterBy[0].value,
    region: defaultValues.region,
    country: defaultValues.country,
  });

  const { data, isFetching, isError } = useGetBestGamesByRetentionQuery({
    category: type,
    retentionPeriod: filters.retentionPeriod,
    cohortJoinPeriod: filters.cohortJoinPeriod,
    limit: LIMITS.byRetention,
    ...(filters.filterBy === 'region'
      ? { region: filters.region }
      : { country: filters.country }),
  });

  const { data: periods, isLoading: periodsLoading } = useGetPeriodsQuery();
  const { data: regions, isLoading: regionsLoading } = useGetRegionsQuery();

  const findRetentionPeriods = useCallback(
    (cohortJoinValue) =>
      periods?.find(
        ({ cohortJoinPeriod }) => cohortJoinPeriod.value === cohortJoinValue,
      )?.retentionPeriods,
    [periods],
  );

  const handleCohortJoinChange = useCallback(
    (name, value) => {
      const currentPeriods = findRetentionPeriods(value);

      const periodExists = currentPeriods?.find(
        ({ value }) => value === filters.retentionPeriod,
      );

      setFilters((prev) => ({
        ...prev,
        [name]: value,
        ...(!periodExists && { retentionPeriod: defaultValues.period }),
      }));
    },
    [findRetentionPeriods, filters.retentionPeriod],
  );

  const cohortJoinPeriods = useMemo(
    () => periods?.map(({ cohortJoinPeriod }) => cohortJoinPeriod),
    [periods],
  );

  const retentionPeriods = useMemo(
    () => findRetentionPeriods(filters.cohortJoinPeriod),
    [findRetentionPeriods, filters.cohortJoinPeriod],
  );

  const countries = useMemo(() => {
    const all = regions?.flatMap(({ countries }) => countries);
    const unique = all?.reduce(
      (acc, country) =>
        !acc.find(({ code }) => country.code === code)
          ? [...acc, country]
          : acc,
      [],
    );

    return sortCountryOptions(mapOptions(unique));
  }, [regions]);

  const geoOptions = useMemo(() => {
    switch (filters.filterBy) {
      case 'region':
        return [
          {
            title: 'Region',
            label: 'region',
            value: filters.region,
            items: mapOptions(regions?.map(({ region }) => region)),
            handleSelect: handleChange,
            loading: regionsLoading,
          },
        ];
      case 'country':
        return [
          {
            title: 'Country',
            label: 'country',
            value: filters.country,
            items: countries,
            handleSelect: handleChange,
            loading: regionsLoading,
            search: true,
          },
        ];
      default:
        return [];
    }
  }, [filters, handleChange, regions, regionsLoading, countries]);

  const filtersOptions = useMemo(
    () => [
      {
        title: 'Choose cohort join month',
        label: 'cohortJoinPeriod',
        value: filters.cohortJoinPeriod,
        items: cohortJoinPeriods,
        handleSelect: handleCohortJoinChange,
        loading: periodsLoading,
      },
      {
        title: 'Choose user retention period',
        label: 'retentionPeriod',
        value: filters.retentionPeriod,
        items: retentionPeriods,
        handleSelect: handleChange,
        loading: periodsLoading,
      },
      // TODO: return back when filter by country logic will be needed
      // {
      //   title: 'Filter by',
      //   label: 'filterBy',
      //   value: filters.filterBy,
      //   items: OPTIONS.retentionFilterBy,
      //   handleSelect: handleChange,
      // },
      ...geoOptions,
    ],
    [
      filters,
      handleChange,
      cohortJoinPeriods,
      handleCohortJoinChange,
      retentionPeriods,
      periodsLoading,
      geoOptions,
    ],
  );

  const games = useMemo(
    () => appendTopGameColors(data, TOP_GAME_COLORS.byRetention),
    [data],
  );

  return (
    <Section className={styles.section}>
      <SectionHeader
        headings={
          <>
            <Typography type="heading-md" weight="medium" uppercase>
              {LIMITS.byRetention} Best games by retention
            </Typography>
            <Typography type="text-lg" color="grey">
              This dashboard highlights the top-performing games with the
              highest retention rates, both in the short and long term
            </Typography>
          </>
        }
        filters={
          <>
            <FilterToggleGroup
              name="type"
              value={type}
              options={OPTIONS.gameTypes}
              onChange={handleChange}
            />
            <Filters options={filtersOptions} />
          </>
        }
      />
      {!isFetching ? (
        !isError && games?.length ? (
          <SectionContent type="bars">
            <div className={styles.listWrapper}>
              <GamesList games={games} />
              <GameBars type="horizontal" games={games} />
            </div>
          </SectionContent>
        ) : (
          <NoData />
        )
      ) : (
        <Loader />
      )}
    </Section>
  );
};

export default memo(GamesByRetention);
